import { useState, useEffect } from "react";
import { DragDropContext, Draggable } from "react-beautiful-dnd";
import StrictModeDroppable from "../../components/StrictModeDroppable";
import { clubService } from "../../services/api";
import toast from "react-hot-toast";
import { usePermission } from "../../hooks/usePermission";
import { formatGroupedNumber } from "../../utils/currencyFormatter";

const ChangePlayerAvailability = () => {
  const [players, setPlayers] = useState({
    notAvailable: [],
    forSale: [],
    forLoan: [],
  });
  const [isLoading, setIsLoading] = useState(true);

  // Permission checks
  const canEditSquad = usePermission("edit_squad");
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [playerFees, setPlayerFees] = useState({}); // Store fees for each player
  const [editingPlayer, setEditingPlayer] = useState(null); // Track which player is being edited
  const [tempFeeValue, setTempFeeValue] = useState(""); // Temporary fee value during editing

  // Fetch squad on mount
  useEffect(() => {
    const fetchSquad = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await clubService.getMyClubSquad();
        if (response.success) {
          const squad = response.data || [];
          const initialFees = {};

          const categorized = {
            forSale: [],
            forLoan: [],
            notAvailable: [],
          };

          squad.forEach((p) => {
            const playerObj = {
              id: p.id,
              name: p.name,
              position: p.position || "Position not set",
              image: p?.image_url || p.image_path,
            };

            // Initialize fees if player has them - convert string to number
            if (p.transfer_fee !== undefined && p.transfer_fee !== null) {
              initialFees[p.id] = {
                ...initialFees[p.id],
                transferFee: parseFloat(p.transfer_fee),
              };
            }
            if (p.loan_fee !== undefined && p.loan_fee !== null) {
              initialFees[p.id] = {
                ...initialFees[p.id],
                loanFee: parseFloat(p.loan_fee),
              };
            }

            if (p.is_available_for_sale) {
              categorized.forSale.push(playerObj);
            } else if (p.is_available_for_loan) {
              categorized.forLoan.push(playerObj);
            } else {
              categorized.notAvailable.push(playerObj);
            }
          });

          setPlayers(categorized);
          setPlayerFees(initialFees);
        } else {
          setError(response.error);
        }
      } catch (err) {
        setError("Failed to load squad");
      } finally {
        setIsLoading(false);
      }
    };

    fetchSquad();
  }, []);

  const handleDragEnd = (result) => {
    // Don't allow drag if user doesn't have edit permission
    if (!canEditSquad) {
      return;
    }

    const { source, destination } = result;

    if (!destination) {
      return;
    }

    if (
      destination.droppableId === source.droppableId &&
      destination.index === source.index
    ) {
      return;
    }

    const start = players[source.droppableId];
    const finish = players[destination.droppableId];

    if (start === finish) {
      const newItems = Array.from(start);
      const [removed] = newItems.splice(source.index, 1);
      newItems.splice(destination.index, 0, removed);

      setPlayers({
        ...players,
        [source.droppableId]: newItems,
      });
    } else {
      const sourceItems = Array.from(start);
      const [removed] = sourceItems.splice(source.index, 1);
      const destinationItems = Array.from(finish);
      destinationItems.splice(destination.index, 0, removed);

      setPlayers({
        ...players,
        [source.droppableId]: sourceItems,
        [destination.droppableId]: destinationItems,
      });
    }
  };

  const handleEditFee = (playerId, feeType) => {
    const currentFee = playerFees[playerId]?.[feeType] || "";
    setEditingPlayer({ playerId, feeType });
    setTempFeeValue(formatFee(currentFee));
  };

  const handleSaveFee = () => {
    const { playerId, feeType } = editingPlayer;
    // Strip the grouping before parsing. Both separators are accepted: the
    // apostrophe this field writes now, and the comma it wrote before
    // 2026-08-02 — a half-edited value, or one pasted from an older screen,
    // must still parse. Leaving the comma out here would have turned
    // "12'500'000" into 12, because parseInt stops at the first apostrophe.
    const numericValue = tempFeeValue
      ? parseInt(tempFeeValue.replace(/[,']/g, ""))
      : null;

    setPlayerFees((prev) => ({
      ...prev,
      [playerId]: {
        ...prev[playerId],
        [feeType]: numericValue,
      },
    }));

    setEditingPlayer(null);
    setTempFeeValue("");
  };

  const handleCancelEdit = () => {
    setEditingPlayer(null);
    setTempFeeValue("");
  };

  // Sale/loan fee, shown in the row AND used as the edit field's mask. It used
  // to hand-roll its grouping with a lookahead regex — the only such regex in
  // the repo — which pinned a comma no matter what every other money surface
  // did. It now uses the platform's one grouping helper (Marin, 2026-08-02:
  // apostrophes for every currency), so the fee a club edits here reads the
  // same as the fee it sees on a request card.
  const formatFee = (value) => {
    if (value === null || value === undefined || value === "") return "";
    return formatGroupedNumber(String(value).replace(/[,']/g, ""));
  };

  const handleConfirmChanges = async () => {
    setSaving(true);
    setSaveError(null);

    // Validate that players in forSale and forLoan have fees set
    const validationErrors = [];

    // Check for sale players
    players.forSale.forEach((player) => {
      const fees = playerFees[player.id];
      if (!fees || fees.transferFee === undefined || fees.transferFee === null) {
        validationErrors.push(`${player.name} needs a transfer fee set`);
      }
    });

    // Check for loan players
    players.forLoan.forEach((player) => {
      const fees = playerFees[player.id];
      if (!fees || fees.loanFee === undefined || fees.loanFee === null) {
        validationErrors.push(`${player.name} needs a loan fee set`);
      }
    });

    // If there are validation errors, show toast and return
    if (validationErrors.length > 0) {
      const errorMessage = validationErrors.join(", ");
      toast.error(`Please set fees for all players: ${errorMessage}`);
      setSaving(false);
      return;
    }

    // Build array of player updates
    const playerUpdates = [];

    // Process all players and their statuses
    [...players.notAvailable, ...players.forSale, ...players.forLoan].forEach(
      (player) => {
        let status = "not_available";
        if (players.forSale.find((p) => p.id === player.id)) {
          status = "available_for_transfer";
        } else if (players.forLoan.find((p) => p.id === player.id)) {
          status = "available_for_loan";
        }

        const updateObj = {
          player_id: player.id,
          status: status,
        };

        // Add fees if set
        const fees = playerFees[player.id];
        if (
          status === "available_for_transfer" &&
          fees &&
          fees.transferFee !== undefined &&
          fees.transferFee !== null
        ) {
          updateObj.transfer_fee = fees.transferFee;
        }
        if (
          status === "available_for_loan" &&
          fees &&
          fees.loanFee !== undefined &&
          fees.loanFee !== null
        ) {
          updateObj.loan_fee = fees.loanFee;
        }

        playerUpdates.push(updateObj);
      }
    );

    console.log("playerUpdates", playerUpdates);
    // return

    try {
      const response = await clubService.updatePlayerAvailability(
        playerUpdates
      );
      if (!response.success) {
        setSaveError(response.error || "Failed to update");
      } else if (response.errors && response.errors.length > 0) {
        setSaveError(
          `Some updates failed: ${response.errors
            .map((e) => e.error)
            .join(", ")}`
        );
      }
    } catch (err) {
      setSaveError("Failed to update");
    } finally {
      setSaving(false);
    }
  };

  const PlayerCard = ({ player, columnType }) => {
    const isEditing = editingPlayer?.playerId === player.id;
    const feeType = columnType === "forSale" ? "transferFee" : "loanFee";
    const feeLabel = columnType === "forSale" ? "Max Transfer fee" : "Loan fee";
    const currentFee = playerFees[player.id]?.[feeType];
    const hasFee = currentFee !== undefined && currentFee !== null;
    const isPriceOnRequest = hasFee && Number(currentFee) === 0;
    const isMissingFee =
      (columnType === "forSale" || columnType === "forLoan") && !hasFee;

    return (
      <div
        className={`bg-white rounded-lg p-4 shadow-sm border ${
          isMissingFee ? "border-red-300 bg-red-50" : "border-gray-200"
        }`}
      >
        <div className="flex items-center gap-4 min-w-0">
          {player.image ? (
            <img
              src={player.image}
              alt={player.name}
              className="w-12 h-12 rounded-full object-cover flex-shrink-0"
              onError={(e) => {
                e.target.style.display = "none";
                e.target.nextSibling.style.display = "flex";
              }}
            />
          ) : null}
          <div
            className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center text-sm font-bold text-gray-600 flex-shrink-0"
            style={{ display: player.image ? "none" : "flex" }}
          >
            {player.name?.charAt(0) || "P"}
          </div>
          {/* min-w-0 lets this flex item shrink so the name's `truncate`
              engages at narrow/half-screen widths instead of overflowing. */}
          <div className="flex-1 min-w-0">
            <h3 className="font-medium text-gray-900 truncate">
              {player.name}
            </h3>
            <p className="text-sm text-gray-500 truncate">{player.position}</p>
          </div>
        </div>

        {/* Fee section for sale/loan columns */}
        {(columnType === "forSale" || columnType === "forLoan") && (
          <div className="mt-3">
            {isEditing ? (
              <div className="relative">
                {/* The placeholder is an EXAMPLE of the format, so it has to be
                    written in it. It used to read "€ 2,40,0000" — comma
                    grouping, and Indian lakh grouping at that, left over from
                    when the Rupee was on the currency list. */}
                <input
                  type="text"
                  placeholder={`€ 2'400'000`}
                  value={tempFeeValue}
                  onChange={(e) => setTempFeeValue(e.target.value)}
                  className="w-full px-3 py-2 pr-16 border border-teal-500 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                  autoFocus
                />
                <div className="absolute right-2 top-1/2 transform -translate-y-1/2 flex items-center gap-1">
                  <button
                    onClick={handleSaveFee}
                    className="text-teal-500 hover:text-teal-600 p-1"
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </button>
                  <button
                    onClick={handleCancelEdit}
                    className="text-gray-400 hover:text-gray-600 p-1"
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            ) : (
              <div
                className={`flex items-center justify-between p-2 rounded-lg ${
                  isMissingFee
                    ? "bg-red-100 border border-red-200"
                    : "bg-[#F8F9FB]"
                }`}
              >
                <div className="flex items-center gap-2">
                  <p
                    className={`text-sm ${
                      isMissingFee ? "text-red-700" : "text-[#1E1E1E]"
                    }`}
                  >
                    {feeLabel}:
                  </p>
                  <p
                    className={`text-sm ${
                      isMissingFee
                        ? "text-red-700 font-medium"
                        : "text-[#1E1E1E]"
                    }`}
                  >
                    {hasFee
                      ? isPriceOnRequest
                        ? "Price on Request"
                        : `€ ${formatFee(currentFee)}`
                      : "Not set"}
                  </p>
                  {isMissingFee && (
                    <span className="text-xs text-red-600 bg-red-200 px-2 py-1 rounded-full">
                      Required
                    </span>
                  )}
                  {isPriceOnRequest && (
                    <span className="text-xs text-blue-600 bg-blue-100 px-2 py-1 rounded-full">
                      Price on Request
                    </span>
                  )}
                </div>
                <button
                  onClick={() => handleEditFee(player.id, feeType)}
                  className={`p-1 ${
                    isMissingFee
                      ? "text-red-500 hover:text-red-600"
                      : "text-gray-400 hover:text-gray-600"
                  }`}
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M11.3333 1.99955C11.5083 1.82445 11.7162 1.68556 11.945 1.5908C12.1738 1.49604 12.419 1.44727 12.6666 1.44727C12.9142 1.44727 13.1594 1.49604 13.3882 1.5908C13.617 1.68556 13.8248 1.82445 13.9999 1.99955C14.175 2.17465 14.3139 2.38252 14.4087 2.61129C14.5034 2.84006 14.5522 3.08526 14.5522 3.33288C14.5522 3.58051 14.5034 3.8257 14.4087 4.05448C14.3139 4.28325 14.175 4.49112 13.9999 4.66622L4.99992 13.6662L1.33325 14.6662L2.33325 10.9996L11.3333 1.99955Z"
                      stroke={isMissingFee ? "#DC2626" : "#2C2C2C"}
                      stroke-width="1.6"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="pb-4 bg-[#F8F9FB]">
      {/* Header */}
      {/* Title and the two actions sat in one unwrapped row and pushed the page
          into horizontal scroll at 375px — the last 4px of it once the board
          below stopped overflowing. Wrapping lets the actions drop to their own
          line instead of pushing; from 768 up they still share one line. */}
      {/* px-12 alone spends 96px of a 375px viewport on gutters, which is what
          squeezed the player names on the board below. Same padding step the
          sibling club pages use (RolesAndResponsibilities.js:783/788/836/842);
          from sm up the 48px gutter is unchanged. */}
      <div className="flex items-center justify-between gap-3 flex-wrap bg-white px-4 sm:px-12 py-4">
        <div className="flex items-center gap-3">
          <button
            onClick={() => window.history.back()}
            className="text-gray-500 hover:text-gray-700"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 14 14"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M12.8337 7.0013H1.16699M1.16699 7.0013L7.00033 12.8346M1.16699 7.0013L7.00033 1.16797"
                stroke="#2C2C2C"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </button>
          <h1 className="text-xl font-bold text-[#202630]">
            Change player availability
          </h1>
        </div>
        <div className="flex items-center gap-4">
          <button
            onClick={() => window.history.back()}
            className="px-4 py-2 border border-[#E6E6E6] rounded-lg text-[#1E1E1E]"
          >
            Cancel
          </button>
          <button
            className={`px-4 py-2 rounded-lg ${
              canEditSquad
                ? "bg-[#2283FF] text-white hover:bg-blue-600"
                : "bg-gray-400 text-gray-600 cursor-not-allowed"
            }`}
            onClick={handleConfirmChanges}
            disabled={saving || !canEditSquad}
          >
            {saving ? "Saving..." : "Confirm changes"}
          </button>
        </div>
      </div>

      {saveError && (
        <div className=" text-red-600 text-sm font-medium text-center  bg-white px-4 sm:px-12 py-4">
          {saveError}
        </div>
      )}

      <p className="text-[#1A212FE5] text-sm font-medium mb-8  bg-white px-4 sm:px-12 py-4">
        Drag and drop players from not available to sale or loan.
      </p>

      {isLoading ? (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full mr-3"></div>
          <span className="text-gray-500">Loading players...</span>
        </div>
      ) : error ? (
        <div className="text-center py-12 text-red-500">{error}</div>
      ) : (
        <div className="px-4 sm:px-12 py-4 ">
          <DragDropContext onDragEnd={handleDragEnd}>
            {/* Three fixed columns on every viewport squeezed the drop zones to
                72px at 375px and 203px at 768px — names rendered 0px and 21px
                wide. The zones stack below lg and keep the desktop board
                untouched from lg up. */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8  ">
              {/* Not Available Column */}
              <div className="bg-white p-6 rounded-lg">
                <div className="mb-6">
                  <span className="px-4 py-3 text-sm font-medium bg-[#FF2D55] text-white rounded-lg">
                    NOT AVAILABLE
                  </span>
                </div>
                <StrictModeDroppable droppableId="notAvailable">
                  {(provided, snapshot) => (
                    <div
                      {...provided.droppableProps}
                      ref={provided.innerRef}
                      className={`space-y-4 min-h-[500px] p-4 rounded-lg border-2 border-dashed ${
                        snapshot.isDraggingOver
                          ? "border-blue-500 bg-blue-50"
                          : "border-gray-200"
                      }`}
                    >
                      {players.notAvailable.map((player, index) => (
                        <Draggable
                          key={player.id}
                          draggableId={String(player.id)}
                          index={index}
                        >
                          {(provided, snapshot) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                              style={{
                                ...provided.draggableProps.style,
                                opacity: snapshot.isDragging ? 0.5 : 1,
                              }}
                            >
                              <PlayerCard
                                player={player}
                                columnType="notAvailable"
                              />
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </div>
                  )}
                </StrictModeDroppable>
              </div>

              {/* For Sale Column */}
              <div className="bg-white p-6 rounded-lg">
                <div className="mb-6">
                  <span className="px-4 py-3 text-sm font-medium bg-[#049669] text-white rounded-lg">
                    FOR SALE
                  </span>
                </div>
                <StrictModeDroppable droppableId="forSale">
                  {(provided, snapshot) => (
                    <div
                      {...provided.droppableProps}
                      ref={provided.innerRef}
                      className={`space-y-4 min-h-[500px] p-4 rounded-lg border-2 border-dashed ${
                        snapshot.isDraggingOver
                          ? "border-emerald-500 bg-emerald-50"
                          : "border-gray-200"
                      }`}
                    >
                      {players.forSale.map((player, index) => (
                        <Draggable
                          key={player.id}
                          draggableId={String(player.id)}
                          index={index}
                        >
                          {(provided, snapshot) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                              style={{
                                ...provided.draggableProps.style,
                                opacity: snapshot.isDragging ? 0.5 : 1,
                              }}
                            >
                              <PlayerCard
                                player={player}
                                columnType="forSale"
                              />
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </div>
                  )}
                </StrictModeDroppable>
              </div>

              {/* For Loan Column */}
              <div className="bg-white p-6 rounded-lg">
                <div className="mb-6">
                  <span className="px-4 py-3 text-sm font-medium bg-[#FF9500] text-white rounded-lg">
                    FOR LOAN
                  </span>
                </div>
                <StrictModeDroppable droppableId="forLoan">
                  {(provided, snapshot) => (
                    <div
                      {...provided.droppableProps}
                      ref={provided.innerRef}
                      className={`space-y-4 min-h-[500px] p-4 rounded-lg border-2 border-dashed ${
                        snapshot.isDraggingOver
                          ? "border-orange-500 bg-orange-50"
                          : "border-gray-200"
                      }`}
                    >
                      {players.forLoan.map((player, index) => (
                        <Draggable
                          key={player.id}
                          draggableId={String(player.id)}
                          index={index}
                        >
                          {(provided, snapshot) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                              style={{
                                ...provided.draggableProps.style,
                                opacity: snapshot.isDragging ? 0.5 : 1,
                              }}
                            >
                              <PlayerCard
                                player={player}
                                columnType="forLoan"
                              />
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </div>
                  )}
                </StrictModeDroppable>
              </div>
            </div>
          </DragDropContext>
        </div>
      )}
    </div>
  );
};

export default ChangePlayerAvailability;
